/**
 * Workspace Standards Page
 *
 * Lists all workspace-scoped standards with search, status filtering,
 * and upload capability. Each card navigates to the standard detail view.
 */

import { useState, useMemo } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Upload, SearchX } from "lucide-react";
import { api } from "convex/_generated/api";
import { useWorkspaceQuery } from "@/hooks/use-workspace-query";
import { PageHeader, StateCard } from "@/components/ui";
import { ConfirmDeleteDialog } from "@/components/ui/ConfirmDeleteDialog";
import { Button } from "@/components/ui/primitives";
import { useTranslation } from "@/packages/utils";
import { useApiMutation } from "@/packages/features-shared";
import { WorkspaceStandardCard } from "./WorkspaceStandardCard";
import { UploadWorkspaceStandardDialog } from "./UploadWorkspaceStandardDialog";
import { EditWorkspaceStandardDialog } from "./EditWorkspaceStandardDialog";
import type { FunctionReturnType } from "convex/server";

type WorkspaceStandard = FunctionReturnType<
  typeof api.workspace_standards_queries.listByWorkspace
>[number];

type StatusFilter = "all" | "extracting" | "extracted" | "extraction_failed";

const STATUS_FILTERS: StatusFilter[] = [
  "all",
  "extracting",
  "extracted",
  "extraction_failed",
];

export function WorkspaceStandardsPage() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { workspaceSlug } = useParams({ strict: false });

  const { data: standards, isLoading } = useWorkspaceQuery(
    api.workspace_standards_queries.listByWorkspace,
  );

  const [searchValue, setSearchValue] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [editStandard, setEditStandard] = useState<WorkspaceStandard | null>(
    null,
  );
  const [deleteStandard, setDeleteStandard] =
    useState<WorkspaceStandard | null>(null);

  const deleteMutation = useApiMutation(
    api.workspace_standards_mutations.deleteStandard,
    {
      name: "delete-workspace-standard",
      successMessage: t("workspaceStandards.detail.deleteSuccess"),
    },
  );

  const handleConfirmDelete = async () => {
    if (!deleteStandard) return;
    await deleteMutation.mutateAsync({ standardId: deleteStandard._id });
    setDeleteStandard(null);
  };

  const filteredStandards = useMemo(() => {
    if (!standards) return [];

    return standards.filter((s) => {
      if (searchValue) {
        const q = searchValue.toLowerCase();
        if (!s.fileName.toLowerCase().includes(q)) return false;
      }

      if (statusFilter !== "all" && s.status !== statusFilter) {
        return false;
      }

      return true;
    });
  }, [standards, searchValue, statusFilter]);

  const isEmpty = !isLoading && standards && standards.length === 0;
  const noResults =
    !isLoading &&
    !isEmpty &&
    filteredStandards.length === 0 &&
    (searchValue || statusFilter !== "all");

  const handleCardClick = (standard: WorkspaceStandard) => {
    navigate({
      to: "/$workspaceSlug/standards/$standardId",
      params: {
        workspaceSlug: workspaceSlug ?? "",
        standardId: standard._id,
      },
    });
  };

  const STATUS_LABEL_KEYS: Record<StatusFilter, string> = {
    all: t("workspaceStandards.status.all"),
    extracting: t("workspaceStandards.status.extracting"),
    extracted: t("workspaceStandards.status.extracted"),
    extraction_failed: t("workspaceStandards.status.extraction_failed"),
  };

  const statusLabel = (filter: StatusFilter): string => {
    return STATUS_LABEL_KEYS[filter];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={t("workspaceStandards.title")}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder={t("workspaceStandards.search")}
        actionSlot={
          <Button
            variant="primary"
            icon={Upload}
            onClick={() => setShowUpload(true)}
          >
            {t("workspaceStandards.uploadButton")}
          </Button>
        }
        filterSlot={
          <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter}
                type="button"
                onClick={() => setStatusFilter(filter)}
                className={`rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors cursor-pointer ${
                  statusFilter === filter
                    ? "bg-accent/10 text-accent"
                    : "text-muted hover:text-primary"
                }`}
              >
                {statusLabel(filter)}
              </button>
            ))}
          </div>
        }
      />

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <StateCard variant="loading">
            <StateCard.Icon />
            <StateCard.Title>{t("workspaceStandards.loading")}</StateCard.Title>
          </StateCard>
        </div>
      )}

      {/* Empty state — truly no data */}
      {isEmpty && (
        <div className="flex items-center justify-center h-full">
          <StateCard variant="empty">
            <StateCard.Icon />
            <StateCard.Title>
              {t("workspaceStandards.emptyTitle")}
            </StateCard.Title>
            <StateCard.Description>
              {t("workspaceStandards.empty")}
            </StateCard.Description>
            <StateCard.Action>
              <button
                type="button"
                onClick={() => setShowUpload(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent/90 transition-colors cursor-pointer"
              >
                <Upload size={14} />
                {t("workspaceStandards.uploadButton")}
              </button>
            </StateCard.Action>
          </StateCard>
        </div>
      )}

      {/* No filter results */}
      {noResults && (
        <div className="flex items-center justify-center py-16">
          <StateCard variant="empty">
            <StateCard.Icon />
            <StateCard.Title>
              <SearchX size={16} className="inline mr-1" />
              {t("workspaceStandards.noResults")}
            </StateCard.Title>
          </StateCard>
        </div>
      )}

      {/* Standards grid */}
      {!isLoading && !isEmpty && filteredStandards.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredStandards.map((standard) => (
            <WorkspaceStandardCard
              key={standard._id}
              standard={standard}
              onClick={() => handleCardClick(standard)}
              onEdit={() => setEditStandard(standard)}
              onDelete={() => setDeleteStandard(standard)}
            />
          ))}
        </div>
      )}

      {/* Upload dialog */}
      <UploadWorkspaceStandardDialog
        open={showUpload}
        onClose={() => setShowUpload(false)}
      />

      <EditWorkspaceStandardDialog
        open={editStandard !== null}
        onClose={() => setEditStandard(null)}
        standard={editStandard}
      />

      <ConfirmDeleteDialog
        open={deleteStandard !== null}
        onClose={() => setDeleteStandard(null)}
        onConfirm={handleConfirmDelete}
        loading={deleteMutation.isPending}
        title={t("workspaceStandards.delete.title")}
        description={t("workspaceStandards.delete.confirm")}
        confirmMatchText={deleteStandard?.fileName ?? ""}
        confirmLabel={t("workspaceStandards.card.actions.delete")}
      />
    </div>
  );
}
